import { useQuery } from '@tanstack/react-query';
import { DEFAULT_CUSTOM_SANDBOX_SIZE_LIMITS } from '@runloop/reflex-contract';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

export function useConfig() {
  return useQuery({
    queryKey: queryKeys.config,
    queryFn: api.getConfig,
    staleTime: Infinity,
  });
}

export function useTunnelUrl(tunnelKey: string | null | undefined, port: number) {
  const { data } = useConfig();
  if (!tunnelKey || !data?.tunnelDomain) return null;
  return `https://${port}-${tunnelKey}.${data.tunnelDomain}`;
}

export function usePlatformUrl() {
  const { data } = useConfig();
  return data?.platformUrl ?? null;
}

/**
 * Web origin for the deployment's GitHub host. Falls back to github.com while
 * the config query is in flight or on a non-enterprise deployment. Build
 * repository links as `${useGitHubWebBaseUrl()}/${owner/repo}` so GitHub
 * Enterprise Server users land on their host instead of github.com.
 */
export function useGitHubWebBaseUrl() {
  const { data } = useConfig();
  return data?.githubWebBaseUrl ?? 'https://github.com';
}

/**
 * Effective maximums for org-defined custom sandbox sizes on this
 * deployment. The server combines deployment defaults with Reflex service
 * settings. Falls back to the documented Runloop ceilings while the config
 * query is in flight or against an older server that doesn't send the field.
 */
export function useCustomSandboxSizeLimits() {
  const { data } = useConfig();
  return data?.customSandboxSizeLimits ?? DEFAULT_CUSTOM_SANDBOX_SIZE_LIMITS;
}
